import build from 'redux-object';
import moment from 'moment-timezone';
import { createSelector } from 'reselect';

export const isResourceCreateExperienceEnabled = (state) =>
  state.initialProps.partner.isResourceCreateExperienceEnabled;
export const isResourceCancelBookingsEnabled = (state) =>
  state.initialProps.partner.isResourceCancelBookingsEnabled;
export const getPartnerTranslations = (state) =>
  state.initialProps.partner.translations;
export const getPartnerI18nList = (state) =>
  state.initialProps.partner.i18nList;

export const getPartnerIsPhoneRequired = (state) =>
  state.initialProps.partner.isPhoneRequired;
export const getPartnerIsPhotoRequired = (state) =>
  state.initialProps.partner.isPhotoRequired;
export const getPartnerIsBioRequired = (state) =>
  state.initialProps.partner.isBioRequired;

export const getLinkColor = (state) => state.initialProps.brand.linkColor;

export const getLocation = (state) => state.router.location;
export const getRouterAction = (state) => state.router.action;

export const experienceReducer = (state) => state.experienceReducer || {};

export const getExperience = createSelector(
  experienceReducer,
  (_experienceReducer) => _experienceReducer.experience
);

export const getOnlineMeeting = createSelector(
  experienceReducer,
  (_experienceReducer) => _experienceReducer.onlineMeeting
);

// Tour steps / form
export const getTourStepForm = (state) => experienceReducer(state).tourStepForm;
export const getContinueToStep = (state) =>
  experienceReducer(state).continueToStep;

export const getSteps = (state) => experienceReducer(state).steps;

export const getAboutCompleted = createSelector(getExperience, (experience) => {
  if (!experience) return false;
  return ['name', 'tagline', 'description'].every(
    (step) => experience[step] !== ''
  );
});

export const getDetailsCompleted = createSelector(
  getExperience,
  getOnlineMeeting,
  (experience, onlineMeeting) => {
    if (!experience) return false;
    const { location, length, maxCapacity, pricingDefinitions, isOnline } =
      experience;
    const complete =
      !!(location || isOnline) &&
      !!length &&
      !!maxCapacity &&
      pricingDefinitions.length > 0 &&
      (!isOnline || !!onlineMeeting?.vendorMeetingUrl);
    return complete;
  }
);

export const getImages = (state) => experienceReducer(state).images;
export const getPhotosCompleted = createSelector(
  getImages,
  (images) => images.length > 0
);

export const getAllStepsCompleted = createSelector(
  getAboutCompleted,
  getDetailsCompleted,
  getPhotosCompleted,
  (aboutCompleted, detailsCompleted, photosCompleted) =>
    aboutCompleted && detailsCompleted && photosCompleted
);

// Experiences

export const getExperiencesFetching = (state) =>
  experienceReducer(state).experiencesFetching;
export const getExperienceUpdating = (state) =>
  experienceReducer(state).experienceUpdating;
export const getExperiences = (state) =>
  build(experienceReducer(state), 'tour').sort((a, b) =>
    a.id < b.id ? 1 : -1
  ) || [];

export const getExperiencesByLastUpdated = (state) =>
  build(experienceReducer(state), 'tour').sort((a, b) =>
    a.updatedAt < b.updatedAt ? 1 : -1
  ) || [];
// get the experience ID for the current experience the user has chosen
export const getSelectedExperienceId = (state) =>
  experienceReducer(state).selectedExperienceId ||
  (getExperiences(state)[0] || {}).id;

// get an experience object by ID
export const getExperienceById = (experienceId) => (state) =>
  build(
    experienceReducer(state),
    'tour',
    experienceId || getSelectedExperienceId(state)
  );

// get the experience object that user has chosen
export const getSelectedExperience = (state) =>
  getExperienceById(getSelectedExperienceId(state))(state);

// whether or not the availability modal should be visible
export const getAvailabilityModalVisible = (state) =>
  experienceReducer(state).availabilityModalVisible;

// get the TourSchedule object for an experience, by experience ID
export const getExperienceSchedule = (experienceId) => (state) =>
  (getExperienceById(experienceId)(state) || {}).tourSchedule;

export const getExperienceScheduleIsOnline = (experienceId) => (state) =>
  (getExperienceSchedule(experienceId)(state) || {}).isOnline;

// get the array of dates available for a schedule, by experience ID
export const getAvailableDates = (experienceId) => (state) =>
  Object.keys(
    (getExperienceSchedule(experienceId)(state) || {}).availableTimesPerDay ||
      {}
  );

// get the currently selected date for a schedule, defaulting to the first available date if none selected
export const getSelectedDate =
  (...args) =>
  (state) => {
    return (
      experienceReducer(state).selectedDate ||
      getAvailableDates(...args)(state)[0]
    );
  };

// get the available times for a schedule, based on experience ID and date
export const getAvailableTimes = (experienceId, date) => (state) => {
  const schedule = getExperienceSchedule(experienceId)(state) || {};
  return (
    (schedule.availableTimesPerDay || {})[
      date || getSelectedDate(experienceId)(state)
    ] || []
  );
};

// get the currently selected time, or first time for the selected exp/date
export const getSelectedTime =
  (...args) =>
  (state) =>
    experienceReducer(state).selectedTime ||
    getAvailableTimes(...args)(state)[0];

export const isExperienceAvailabilityLoading = (state) =>
  experienceReducer(state).experienceAvailabilityLoading;

export const isSavingSchedule = (state) =>
  experienceReducer(state).savingSchedule;

export const getMobileTabsMenuToggled = (state) =>
  experienceReducer(state).mobileTabsMenuToggled;

// Modals
export const getShowNewExperienceModal = (state) =>
  experienceReducer(state).showNewExperienceModal;
export const getShowResourceMeetingModal = (state) =>
  state.resource.showResourceMeetingModal;

// Resources
export const getResource = (state) => state.resource.resource;
export const getResourceUpdating = (state) => state.resource.resourceUpdating;
export const getSlot = (state) => state.slots.slot;

export const getResourceI18n = (state) => {
  return (
    state.resource &&
    state.resource.resource &&
    state.resource.resource.attributes &&
    state.resource.resource.attributes.i18n
  );
};

// Get resource timezone if saved, otherwise pull browser timezone
export const getResourceTimezone = (state) => {
  const {
    auth: { user },
    resource: { resource },
  } = state;

  if (resource && resource.attributes && resource.attributes.timeZone) {
    return resource.attributes.timeZone;
  }

  if (user && user.data.attributes && user.data.attributes.timeZone) {
    return user.data.attributes.timeZone;
  }

  return moment.tz.guess() || 'America/Los_Angeles';
};
